"use strict";
/*
Copyright 2020 Splunk Inc.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.SplunkErrorInstrumentation = exports.ERROR_INSTRUMENTATION_VERSION = exports.ERROR_INSTRUMENTATION_NAME = void 0;
const shimmer = require("shimmer");
const sdk_trace_web_1 = require("@opentelemetry/sdk-trace-web");
const utils_1 = require("./utils");
const core_1 = require("@opentelemetry/core");
const instrumentation_1 = require("@opentelemetry/instrumentation");
// FIXME take timestamps from events?
const STACK_LIMIT = 4096;
const MESSAGE_LIMIT = 1024;
function useful(s) {
    return s && s.trim() !== '' && !s.startsWith('[object') && s !== 'error';
}
function stringifyValue(value) {
    if (value === undefined) {
        return '(undefined)';
    }
    return value.toString();
}
function addStackIfUseful(span, err) {
    if (err && err.stack && useful(err.stack)) {
        span.setAttribute('error.stack', (0, utils_1.limitLen)(err.stack.toString(), STACK_LIMIT));
    }
}
exports.ERROR_INSTRUMENTATION_NAME = 'errors';
exports.ERROR_INSTRUMENTATION_VERSION = '1';
class SplunkErrorInstrumentation extends instrumentation_1.InstrumentationBase {
    constructor(config) {
        super(exports.ERROR_INSTRUMENTATION_NAME, exports.ERROR_INSTRUMENTATION_VERSION, config);
        this._consoleErrorHandler = (original) => {
            return (...args) => {
                this.report('console.error', args);
                return original.apply(this, args);
            };
        };
        this._unhandledRejectionListener = (event) => {
            this.report('unhandledrejection', event.reason);
        };
        this._errorListener = (event) => {
            this.report('onerror', event);
        };
        this._documentErrorListener = (event) => {
            this.report('eventListener.error', event);
        };
    }
    init() { }
    enable() {
        shimmer.wrap(console, 'error', this._consoleErrorHandler);
        window.addEventListener('unhandledrejection', this._unhandledRejectionListener);
        window.addEventListener('error', this._errorListener);
        document.documentElement.addEventListener('error', this._documentErrorListener, { capture: true });
    }
    disable() {
        shimmer.unwrap(console, 'error');
        window.removeEventListener('unhandledrejection', this._unhandledRejectionListener);
        window.removeEventListener('error', this._errorListener);
        document.documentElement.removeEventListener('error', this._documentErrorListener, { capture: true });
    }
    reportError(source, err) {
        const msg = err.message || err.toString();
        if (!useful(msg) && !err.stack) {
            return;
        }
        const now = (0, core_1.hrTime)();
        const span = this.tracer.startSpan(source, { startTime: now });
        span.setAttribute('component', 'error');
        span.setAttribute('error', true);
        span.setAttribute('error.object', useful(err.name) ? err.name : err.constructor && err.constructor.name ? err.constructor.name : 'Error');
        span.setAttribute('error.message', (0, utils_1.limitLen)(msg, MESSAGE_LIMIT));
        addStackIfUseful(span, err);
        span.end(now);
    }
    reportString(source, message, firstError) {
        if (!useful(message)) {
            return;
        }
        const now = (0, core_1.hrTime)();
        const span = this.tracer.startSpan(source, { startTime: now });
        span.setAttribute('component', 'error');
        span.setAttribute('error', true);
        span.setAttribute('error.object', 'String');
        span.setAttribute('error.message', (0, utils_1.limitLen)(message, MESSAGE_LIMIT));
        if (firstError) {
            addStackIfUseful(span, firstError);
        }
        span.end(now);
    }
    reportErrorEvent(source, ev) {
        if (ev.error) {
            this.report(source, ev.error);
        }
        else if (ev.message) {
            this.report(source, ev.message);
        }
    }
    reportEvent(source, ev) {
        // FIXME consider other sources of global 'error' DOM callback - what else can be captured here?
        if (!ev.target && !useful(ev.type)) {
            return;
        }
        const now = (0, core_1.hrTime)();
        const span = this.tracer.startSpan(source, { startTime: now });
        span.setAttribute('component', 'error');
        span.setAttribute('error.type', ev.type);
        if (ev.target) {
            // TODO: find types to match this
            span.setAttribute('target_element', ev.target.tagName);
            span.setAttribute('target_xpath', (0, sdk_trace_web_1.getElementXPath)(ev.target, true));
            span.setAttribute('target_src', ev.target.src);
        }
        span.end(now);
    }
    report(source, arg) {
        if (Array.isArray(arg) && arg.length === 0) {
            return;
        }
        if (arg instanceof Array && arg.length === 1) {
            arg = arg[0];
        }
        if (arg instanceof Error) {
            this.reportError(source, arg);
        }
        else if (arg instanceof ErrorEvent) {
            this.reportErrorEvent(source, arg);
        }
        else if (arg instanceof Event) {
            this.reportEvent(source, arg);
        }
        else if (typeof arg === 'string') {
            this.reportString(source, arg);
        }
        else if (arg instanceof Array) {
            // if any arguments are Errors then add the stack trace even though the message is handled differently
            const firstError = arg.find(x => x instanceof Error);
            this.reportString(source, arg.map(x => stringifyValue(x)).join(' '), firstError);
        }
        else {
            this.reportString(source, stringifyValue(arg)); // FIXME or JSON.stringify?
        }
    }
}
exports.SplunkErrorInstrumentation = SplunkErrorInstrumentation;
//# sourceMappingURL=SplunkErrorInstrumentation.js.map